import { Link } from '@tanstack/react-router';
import {
  createColumnHelper,
  type PaginationState,
} from '@tanstack/react-table';
import { CircleArrowLeft } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { useStudioStore } from '~entities/studios/model/studio-store';

import {
  useCampaign,
  useCampaignRecipients,
  useCampaigns,
} from '~features/email-builder/api/hooks';
import type { CampaignRecipientsResponse } from '~features/email-builder/model/types';

import {
  EnrollioTable,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { Button } from '~shared/shadcn/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';

const RECIPIENTS_PER_PAGE = 50;
type CampaignRecipient = CampaignRecipientsResponse['items'][number];
const columnHelper = createColumnHelper<CampaignRecipient>();

const pct = (n: number | undefined) => `${Math.round((n ?? 0) * 1000) / 10}%`;

export const EmailCampaignsPage = () => {
  const studioId = useStudioStore((s) => s.currentStudioId);
  const [selectedId, setSelectedId] = useState<string | undefined>();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [recipientsPage, setRecipientsPage] = useState(1);
  const campaignsQuery = useCampaigns(
    statusFilter === 'all' ? undefined : statusFilter,
  );
  const campaignQuery = useCampaign(selectedId);
  const recipientsQuery = useCampaignRecipients(selectedId, {
    page: recipientsPage,
    limit: RECIPIENTS_PER_PAGE,
  });

  // Drop selection when switching studios so we never query another studio's id.
  useEffect(() => {
    setSelectedId(undefined);
    setRecipientsPage(1);
  }, [studioId]);

  const health = campaignQuery.data?.health;
  const stats = campaignQuery.data?.stats;
  const campaigns = (campaignsQuery.data ?? []).filter(
    (campaign) => campaign.status !== 'draft',
  );

  const recipients = recipientsQuery.data?.items ?? [];
  const recipientsTotal = recipientsQuery.data?.total ?? 0;
  const recipientsPageCount = Math.max(
    1,
    Math.ceil(recipientsTotal / RECIPIENTS_PER_PAGE),
  );
  const recipientColumns = useMemo(
    () => [
      columnHelper.accessor('recipientEmail', {
        header: 'Recipient',
        cell: ({ row }) => (
          <div>
            <div>{row.original.recipientName ?? '—'}</div>
            <div className='text-xs text-muted-foreground'>
              {row.original.recipientEmail}
            </div>
          </div>
        ),
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: ({ row }) => (
          <div className='capitalize'>
            {row.original.status ?? 'accepted'}
            {row.original.statusReason ? (
              <div className='text-xs text-muted-foreground'>
                {row.original.statusReason}
              </div>
            ) : null}
          </div>
        ),
      }),
      columnHelper.accessor(
        (row) => row.statusUpdatedAt ?? row.createdAt ?? '',
        {
          id: 'updated',
          header: 'Updated',
          cell: (info) =>
            info.getValue() ? new Date(info.getValue()).toLocaleString() : '—',
        },
      ),
    ],
    [],
  );
  const recipientsPagination: PaginationState = {
    pageIndex: recipientsPage - 1,
    pageSize: RECIPIENTS_PER_PAGE,
  };

  return (
    <div className='flex h-[calc(100vh-4rem)] w-full min-w-0 flex-col'>
      <div className='flex flex-col gap-3 border-b border-border p-4 sm:flex-row sm:items-center'>
        <div className='flex min-w-0 items-center gap-3'>
          <Button variant='ghost' size='icon' asChild>
            <Link to='/email-builder'>
              <CircleArrowLeft className='size-5' />
            </Link>
          </Button>
          <h1 className='min-w-0 text-xl font-semibold sm:text-2xl'>
            Campaign Delivery
          </h1>
        </div>
        <div className='sm:ml-auto'>
          <Button variant='outline' asChild className='w-full sm:w-auto'>
            <Link to='/email-builder'>Compose</Link>
          </Button>
        </div>
      </div>

      <div className='grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[360px_1fr]'>
        <aside className='space-y-3 overflow-y-auto border-r border-border p-4'>
          <Select
            value={statusFilter}
            onValueChange={(value) => {
              setStatusFilter(value);
              setSelectedId(undefined);
              setRecipientsPage(1);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder='Status' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All statuses</SelectItem>
              <SelectItem value='scheduled'>Scheduled</SelectItem>
              <SelectItem value='sending'>Sending</SelectItem>
              <SelectItem value='sent'>Sent</SelectItem>
              <SelectItem value='failed'>Failed</SelectItem>
              <SelectItem value='cancelled'>Cancelled</SelectItem>
            </SelectContent>
          </Select>

          {campaigns.map((campaign) => (
            <button
              key={campaign._id}
              type='button'
              onClick={() => {
                setSelectedId(campaign._id);
                setRecipientsPage(1);
              }}
              className={`block w-full rounded-md border px-3 py-2 text-left text-sm ${
                selectedId === campaign._id
                  ? 'border-primary bg-muted'
                  : 'border-border hover:bg-muted'
              }`}
            >
              <div className='font-medium'>
                {campaign.subject || 'Untitled'}
              </div>
              <div className='text-xs capitalize text-muted-foreground'>
                {campaign.status}
                {campaign.scheduleAt
                  ? ` · ${new Date(campaign.scheduleAt).toLocaleString()}`
                  : ''}
              </div>
            </button>
          ))}
          {!campaigns.length ? (
            <p className='text-sm text-muted-foreground'>
              No campaigns yet. Compose one from Campaign Builder.
            </p>
          ) : null}
        </aside>

        <div className='overflow-y-auto p-6'>
          {!selectedId ? (
            <p className='text-muted-foreground'>
              Select a campaign to view delivery health and recipients.
            </p>
          ) : campaignQuery.isLoading ? (
            <p>Loading…</p>
          ) : campaignQuery.data ? (
            <div className='space-y-6'>
              <div>
                <h2 className='text-xl font-semibold'>
                  {campaignQuery.data.subject || 'Untitled'}
                </h2>
                <p className='text-sm capitalize text-muted-foreground'>
                  {campaignQuery.data.status}
                  {campaignQuery.data.scheduleAt
                    ? ` · ${new Date(campaignQuery.data.scheduleAt).toLocaleString()}`
                    : ''}
                </p>
                {campaignQuery.data.status === 'failed' &&
                campaignQuery.data.failureReason?.trim() ? (
                  <div className='mt-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm'>
                    <p className='font-medium text-destructive'>
                      Failure reason
                    </p>
                    <p className='mt-1 whitespace-pre-wrap break-words text-destructive/90'>
                      {campaignQuery.data.failureReason.trim()}
                    </p>
                    {(stats?.total ?? 0) === 0 ? (
                      <p className='mt-2 text-xs text-muted-foreground'>
                        No recipient logs were created — the send failed before
                        any messages were handed to SendGrid.
                        {campaignQuery.data.recipientCountAtSchedule
                          ? ` ${campaignQuery.data.recipientCountAtSchedule} recipient(s) were queued at schedule time.`
                          : ''}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4'>
                <HealthChip
                  label={(stats?.delivered ?? 0) > 0 ? 'Delivered' : 'Accepted'}
                  value={pct(health?.deliveryRate)}
                  detail={
                    (stats?.delivered ?? 0) > 0
                      ? `${stats?.delivered ?? 0}`
                      : `${stats?.accepted ?? 0}`
                  }
                />
                <HealthChip
                  label='Bounced'
                  value={pct(health?.bounceRate)}
                  detail={`${stats?.bounced ?? 0}`}
                />
                <HealthChip
                  label='DND / unsub'
                  value={pct(health?.dndRate)}
                  detail={`${(stats?.unsubscribed ?? 0) + (stats?.spam ?? 0)}`}
                />
                <HealthChip
                  label='Failed'
                  value={pct(health?.failureRate)}
                  detail={`${stats?.failed ?? 0}`}
                />
              </div>
              {(stats?.accepted ?? 0) > 0 || (stats?.delivered ?? 0) > 0 ? (
                <p className='text-xs text-muted-foreground'>
                  Provider accepted {stats?.accepted ?? 0}
                  {(stats?.delivered ?? 0) > 0
                    ? ` · inbox delivered ${stats?.delivered ?? 0}`
                    : ' (inbox delivery updates when SendGrid events arrive)'}
                  {(stats?.total ?? 0) > 0 ? ` · ${stats?.total} total` : ''}.
                </p>
              ) : null}

              <div>
                <h3 className='mb-2 font-medium'>
                  Recipients
                  {recipientsTotal > 0 ? ` (${recipientsTotal})` : ''}
                </h3>
                <EnrollioTable
                  totalLabel='Campaign recipients'
                  filtersLayout='inline'
                  filterHeader={(table) => (
                    <EnrollioTableSearch
                      table={table}
                      placeholder='Search recipients...'
                    />
                  )}
                  data={recipients}
                  columns={recipientColumns}
                  rowCount={recipientsTotal}
                  manualPagination
                  pagination={recipientsPagination}
                  onPaginationChange={(updater) => {
                    const next =
                      typeof updater === 'function'
                        ? updater(recipientsPagination)
                        : updater;
                    setRecipientsPage(
                      Math.min(recipientsPageCount, next.pageIndex + 1),
                    );
                  }}
                  getRowId={(row) => row._id}
                  isUpdating={recipientsQuery.isFetching}
                  emptyContent='No recipient logs yet.'
                  ariaLabel='Campaign recipients'
                />
              </div>
            </div>
          ) : (
            <p className='text-destructive'>Campaign not found</p>
          )}
        </div>
      </div>
    </div>
  );
};

const HealthChip = ({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail: string;
}) => (
  <div className='rounded-md border border-border p-3'>
    <div className='text-xs uppercase tracking-wide text-muted-foreground'>
      {label}
    </div>
    <div className='text-2xl font-semibold'>{value}</div>
    <div className='text-xs text-muted-foreground'>{detail} messages</div>
  </div>
);
